import type { FC, ReactNode } from 'react'

interface UnknownElementPlaceholderProps {
  type: string
  children?: ReactNode
}

/**
 * Visible placeholder rendered when an element's `type` is not handled by any
 * loaded plugin. Communicates to the user (and devs) that something is there
 * but the plugin responsible for it is not loaded — instead of silently
 * dropping the data.
 */
export const UnknownElementPlaceholder: FC<UnknownElementPlaceholderProps> = props => {
  const { type, children } = props
  const title = `Unknown element type: ${type}. The plugin that handles this content is not loaded.`
  return (
    <span
      contentEditable={false}
      data-testid="unknown-element-placeholder"
      data-element-type={type}
      title={title}
      style={{
        padding: '0 0.25em',
        margin: '0 0.125em',
        borderRadius: 'var(--ts-radius-sm, 2px)',
        // Theme tokens: the --ts-* ones don't exist, and their black fallbacks were unreadable in dark (GH #2529)
        backgroundColor: 'var(--tuna-palette-action-hover)',
        color: 'var(--tuna-content-muted)',
        fontSize: '0.85em',
        fontFamily: 'var(--ts-font-mono, monospace)',
        userSelect: 'none',
      }}
    >
      {`<${type}>`}
      {children}
    </span>
  )
}

export default UnknownElementPlaceholder
